import {
  useCallback,
  useEffect,
  useState,
} from 'react';

import { Contract } from 'ethers';

import useFiboChainRpc from '@/store/builtInRpc/useFiboChainRpc';

/**
 * 获取报价 Hook 防抖
 * @param lpContract LP 合约实例（需提前创建）
 * @param TokenA     Token A 地址（输入 token）
 * @param TokenB     Token B 地址（输出 token）
 * 
 * 注意：此函数不会判断 LP 是否存在，使用前应先验证 LP 是否有效。
 * 
 * @returns {
 *   quoteLod: boolean - 是否加载中
 *   amountOut: bigint - 兑换输出数量
 * }
 */
const useReserve = (
    lpContract: Contract | null,
    TokenA: string,
    TokenB: string,
) => {
    const [reserveLod, setReserveLoading] = useState(false);        // 加载状态
    const [tokenAReserve, setTokenAReserve] = useState(BigInt(0));      // 计算结果（输出 token 数量）
    const [tokenBReserve, setTokenBReserve] = useState(BigInt(0));      // 计算结果（输出 token 数量）

    const { routerAbiReadOnly } = useFiboChainRpc();            // 获取 Router 只读合约（用于 getAmountOut）

    /**
     * 计算输出数量
     * 步骤：
     * 1. 获取 LP 中两个 token 的储备量（通过 getReserves）
     * 2. 根据 tokenInput 和储备量，调用 Router 的 getAmountOut 方法计算输出
     */
    const getReserve = useCallback(async () => {
        if (routerAbiReadOnly && lpContract) {
            try {
                setReserveLoading(true);
                // 获取 LP 储备量（返回 [reserve0, reserve1]）
                const tokeLpNum = await lpContract.getReserves();

                // 确保 TokenA 和 TokenB 顺序与合约中一致（按地址字典序排序）
                const is = TokenA.toLowerCase() < TokenB.toLowerCase();
                const tokeANum = is ? tokeLpNum[0] : tokeLpNum[1];
                const tokenBNum = is ? tokeLpNum[1] : tokeLpNum[0];
                setTokenAReserve(tokeANum);
                setTokenBReserve(tokenBNum); // 设置输出结果
            } catch (error) {
                console.log('useReserve error', error);
            } finally {
                setReserveLoading(false); // 结束加载状态
            }
        } else {
            setTokenAReserve(BigInt(0));
            setTokenBReserve(BigInt(0));
        }
    }, [TokenA, TokenB, lpContract, routerAbiReadOnly]);
    // 当依赖项变化时重新计算报价
    useEffect(() => {
        getReserve();
    }, [getReserve]);

    return {
        reserveLod,   // 是否正在加载
        tokenAReserve, // 输出代币A的储备量
        tokenBReserve,   // 输出代币B的储备量
        getReserve
    };
};

export default useReserve;